import focusLock from "dom-focus-lock"
import throttle from "lodash-es/throttle"

type Props = {
  topBarSelector: string
  dropdownToggleSelector: string
  dropdownToggleActiveClass: string
  dropdownToggleParentSelector: string
  dropdownSelector: string
  maskActiveClass: string
  hamburgerToggleSelector: string
  mobileActiveClass: string
  menuSelector: string
  backToMainSelector: string
  subMenuSelector: string
  subMenuActiveClass: string
  subMenuToggleSelector: string
  subMenuDropdownSelector: string
  backgroundColor: string
}

export default function masthead(
  el: HTMLElement,
  {
    topBarSelector,
    dropdownToggleSelector,
    dropdownToggleActiveClass,
    dropdownToggleParentSelector,
    dropdownSelector,
    maskActiveClass,
    hamburgerToggleSelector,
    mobileActiveClass,
    menuSelector,
    backToMainSelector,
    subMenuSelector,
    subMenuActiveClass,
    subMenuToggleSelector,
    subMenuDropdownSelector,
    backgroundColor,
  }: Props
) {
  const mediaQuery = window.matchMedia("(min-width: 1200px)")
  checkMedia(mediaQuery)
  mediaQuery.addListener(() => {
    checkMedia(mediaQuery)
  })

  function checkMedia(e: MediaQueryList) {
    let topBarHeight =
      document.querySelector(topBarSelector)?.getBoundingClientRect().height ||
      38
    let dropdownToggleParentEls: NodeListOf<HTMLElement> = el.querySelectorAll(
      dropdownToggleParentSelector
    )
    let dropdownToggleEls: NodeListOf<HTMLElement> = el.querySelectorAll(
      dropdownToggleSelector
    )
    const hamburgerToggleEl: HTMLElement | null = el.querySelector(
      hamburgerToggleSelector
    )
    const menuEl: HTMLElement | null = el.querySelector(menuSelector)
    const bodyEl: HTMLElement | null = document.querySelector("body")

    if (
      dropdownToggleParentEls == null ||
      dropdownToggleEls == null ||
      hamburgerToggleEl == null ||
      menuEl == null ||
      bodyEl == null
    ) {
      return
    }

    let navIsActive = false
    initAllViewportFunctions(topBarHeight, navIsActive)
    if (e.matches) {
      initDesktopFunctions(
        topBarHeight,
        navIsActive,
        dropdownToggleParentEls,
        dropdownToggleEls
      )
      destroyMobileFunctions(navIsActive, hamburgerToggleEl, menuEl, bodyEl)
    } else {
      initMobileFunctions(
        topBarHeight,
        navIsActive,
        dropdownToggleParentEls,
        dropdownToggleEls,
        hamburgerToggleEl,
        menuEl,
        bodyEl
      )
    }
  }

  function initAllViewportFunctions(
    topBarHeight: number,
    navIsActive: boolean
  ) {
    // Set offset initially
    if (window.innerWidth >= 768) {
      setStickyScrollOffset(el, topBarHeight)
    } else {
      setStickyScrollOffset(el, 0)
    }

    if (backgroundColor !== "") {
      // Toggle masthead background color when (throttled) scrolling
      document.addEventListener(
        "scroll",
        throttle(function () {
          if (window.scrollY > topBarHeight) {
            removeNavBgColor(el, backgroundColor)
          } else {
            if (navIsActive === false) {
              addNavBgColor(el, backgroundColor)
            }
          }
        }, 10)
      )

      // Toggle masthead background color on hover
      el.addEventListener("mouseover", function (e) {
        removeNavBgColor(el, backgroundColor)
      })

      el.addEventListener("mouseout", function (e) {
        if (window.scrollY <= topBarHeight) {
          if (navIsActive === false) {
            addNavBgColor(el, backgroundColor)
          }
        }
      })
    }
  }

  function initDesktopFunctions(
    topBarHeight: number,
    navIsActive: boolean,
    dropdownToggleParentEls: NodeListOf<HTMLElement>,
    dropdownToggleEls: NodeListOf<HTMLElement>
  ) {
    // Add active class to nav toggles on hover/focus
    dropdownToggleParentEls.forEach(dropdownToggleParentEl => {
      const dropdownToggleEl: HTMLElement | null = dropdownToggleParentEl.querySelector(
        dropdownToggleSelector
      )
      if (dropdownToggleEl == null) {
        return
      }
      // Mouse enters dropdown toggle target
      dropdownToggleParentEl.addEventListener("mouseenter", function () {
        if (window.innerWidth >= 1200) {
          navIsActive = true
          showDropdown(
            el,
            dropdownToggleParentEls,
            dropdownToggleEl,
            dropdownToggleActiveClass,
            maskActiveClass,
            dropdownToggleSelector
          )
        }
      })

      // Mouse leaves dropdown toggle target
      dropdownToggleParentEl.addEventListener("mouseleave", function () {
        if (window.innerWidth >= 1200) {
          navIsActive = false
          hideDropdown(
            el,
            dropdownToggleEl,
            dropdownToggleActiveClass,
            maskActiveClass
          )
          if (window.scrollY <= topBarHeight && backgroundColor) {
            addNavBgColor(el, backgroundColor)
          }
        }
      })

      // Keyboard focuses on dropdown toggle target
      dropdownToggleEl.addEventListener("focus", function () {
        if (window.innerWidth >= 1200) {
          navIsActive = true
          if (backgroundColor) {
            removeNavBgColor(el, backgroundColor)
          }
          showDropdown(
            el,
            dropdownToggleParentEls,
            dropdownToggleEl,
            dropdownToggleActiveClass,
            maskActiveClass,
            dropdownToggleSelector
          )
        }
      })
      let activeParentEl: HTMLElement
      function trackActiveElement() {
        if (!activeParentEl.contains(document.activeElement)) {
          navIsActive = false
          hideDropdown(
            el,
            dropdownToggleEl,
            dropdownToggleActiveClass,
            maskActiveClass
          )
          document.removeEventListener("keyup", trackActiveElement)
          if (dropdownToggleEl !== null) {
            dropdownToggleEl.setAttribute("aria-expanded", "false")
          }
        }
      }
      // Keyboard blur on dropdown toggle target
      // we want to ensure the dropdown menu is closed when the activeElement is no longer part of this primary menu item
      dropdownToggleEl.addEventListener("blur", function () {
        if (window.innerWidth >= 1200) {
          activeParentEl = dropdownToggleParentEl
          document.addEventListener("keyup", trackActiveElement)
        }
      })
    })
  }

  function initMobileFunctions(
    topBarHeight: number,
    navIsActive: boolean,
    dropdownToggleParentEls: NodeListOf<HTMLElement>,
    dropdownToggleEls: NodeListOf<HTMLElement>,
    hamburgerToggleEl: HTMLElement,
    menuEl: HTMLElement,
    bodyEl: HTMLElement
  ) {
    // Tablet/mobile dropdown menus
    dropdownToggleParentEls.forEach(dropdownToggleParentEl => {
      // Open menu
      const dropdownToggleEl: HTMLElement | null = dropdownToggleParentEl.querySelector(
        dropdownToggleSelector
      )
      const dropdownEl: HTMLElement | null = dropdownToggleParentEl.querySelector(
        dropdownSelector
      )
      const backToMainEl: HTMLElement | null = dropdownToggleParentEl.querySelector(
        backToMainSelector
      )
      if (
        dropdownToggleEl == null ||
        dropdownToggleEl == null ||
        backToMainEl == null
      ) {
        return
      }
      dropdownToggleEl.addEventListener("click", function (e) {
        if (window.innerWidth < 1200) {
          e.preventDefault()
          dropdownToggleEl.classList.add(dropdownToggleActiveClass)
          focusLock.on(dropdownEl)
        }
      })
      // Close menu
      backToMainEl.addEventListener("click", function (e) {
        if (window.innerWidth < 1200) {
          dropdownToggleEl.classList.remove(dropdownToggleActiveClass)
          // Close open submenus
          const subMenuEls = dropdownToggleParentEl.querySelectorAll(
            subMenuSelector
          )
          subMenuEls.forEach(el => {
            if (el.classList.contains(subMenuActiveClass)) {
              el.classList.toggle(subMenuActiveClass)
            }
          })
          const scrollableMenuEl: HTMLElement | null = el.querySelector(
            menuSelector
          )
          if (scrollableMenuEl !== null) {
            scrollableMenuEl.scrollTop = 0
          }
          focusLock.off(dropdownEl)
        }
      })
    })
    // Hamburger (mobile) menu toggle
    hamburgerToggleEl.addEventListener("click", function () {
      if (window.innerWidth < 1200) {
        if (navIsActive === true) {
          hideMobileMenu(
            el,
            mobileActiveClass,
            maskActiveClass,
            hamburgerToggleEl,
            backgroundColor,
            menuEl,
            bodyEl,
            subMenuSelector,
            subMenuActiveClass,
            subMenuToggleSelector,
            dropdownToggleSelector,
            dropdownToggleActiveClass
          )
          navIsActive = false
        } else {
          showMobileMenu(
            el,
            mobileActiveClass,
            maskActiveClass,
            hamburgerToggleEl,
            backgroundColor,
            menuEl,
            bodyEl,
            topBarHeight
          )
          navIsActive = true
        }
      }
    })

    // Mobile sub menus
    const subMenuToggleEls = el.querySelectorAll(subMenuToggleSelector)
    subMenuToggleEls.forEach(el => {
      el.setAttribute("aria-expanded", "false")
    })

    const subMenuEls = el.querySelectorAll(subMenuSelector)
    subMenuEls.forEach(el => {
      let subMenuToggleEl: HTMLDivElement | null = el.querySelector(
        subMenuToggleSelector
      )
      if (subMenuToggleEl) {
        subMenuToggleEl.addEventListener("click", function (e) {
          if (window.innerWidth < 1200) {
            if (el.classList.contains(subMenuActiveClass)) {
              el.classList.remove(subMenuActiveClass)
              this.setAttribute("aria-expanded", "false")
            } else {
              el.classList.add(subMenuActiveClass)
              this.setAttribute("aria-expanded", "true")
            }
          }
        })
      }
    })
  }

  function destroyMobileFunctions(
    navIsActive: boolean,
    hamburgerToggleEl: HTMLElement,
    menuEl: HTMLElement,
    bodyEl: HTMLElement
  ) {
    hideMobileMenu(
      el,
      mobileActiveClass,
      maskActiveClass,
      hamburgerToggleEl,
      backgroundColor,
      menuEl,
      bodyEl,
      subMenuSelector,
      subMenuActiveClass,
      subMenuToggleSelector,
      dropdownToggleSelector,
      dropdownToggleActiveClass
    )
    navIsActive = false
  }
}

function setStickyScrollOffset(el: HTMLElement, height: number) {
  el.style.top = height * -1 + "px"
}

function addNavBgColor(el: HTMLElement, backgroundColor: string) {
  el.classList.add(backgroundColor)
}

function removeNavBgColor(el: HTMLElement, backgroundColor: string) {
  el.classList.remove(backgroundColor)
}

function showDropdown(
  el: HTMLElement,
  dropdownToggleParentEls: NodeListOf<Element> | null,
  dropdownToggleEl: HTMLElement,
  dropdownToggleActiveClass: string,
  maskActiveClass: string,
  dropdownToggleSelector: string
) {
  if (dropdownToggleParentEls) {
    // Hide all open dropdowns (either from mousing or keyboard focus)
    dropdownToggleParentEls.forEach(dropdownToggleParentEl => {
      let dropdownToggleEl: HTMLElement | null = dropdownToggleParentEl.querySelector(
        dropdownToggleSelector
      )
      hideDropdown(
        el,
        dropdownToggleEl,
        dropdownToggleActiveClass,
        maskActiveClass
      )
    })
  }
  // Show selected dropdown
  dropdownToggleEl.classList.add(dropdownToggleActiveClass)
  dropdownToggleEl.setAttribute("aria-expanded", "true")
  el.classList.add(maskActiveClass)
}

function hideDropdown(
  el: HTMLElement,
  dropdownToggleEl: HTMLElement | null,
  dropdownToggleActiveClass: string,
  maskActiveClass: string
) {
  if (dropdownToggleEl) {
    dropdownToggleEl.classList.remove(dropdownToggleActiveClass)
    dropdownToggleEl.setAttribute("aria-expanded", "false")
    el.classList.remove(maskActiveClass)
  }
}

function showMobileMenu(
  el: HTMLElement,
  mobileActiveClass: string,
  maskActiveClass: string,
  hamburgerToggleEl: HTMLElement,
  backgroundColor: string,
  menuEl: HTMLElement,
  bodyEl: HTMLElement,
  topBarHeight: number
) {
  el.classList.add(mobileActiveClass)
  el.classList.add(maskActiveClass)
  hamburgerToggleEl.setAttribute("aria-expanded", "true")
  if (backgroundColor) {
    addNavBgColor(el, backgroundColor)
  }
  bodyEl.classList.add("mobile-menu-open")
  // Adjust the height of the mobile menu on tablet/mobile if the top bar is out of view
  let height: number
  if (window.innerWidth > 767 && window.innerWidth < 1200) {
    if (window.scrollY > topBarHeight) {
      height = window.innerHeight - 68
    } else {
      height = window.innerHeight - 106
    }
    menuEl.style.height = `${height}px`
  } else if (window.innerWidth <= 767) {
    height = window.innerHeight - 53
    menuEl.style.height = `${height}px`
  }
  focusLock.on(el)
}

function hideMobileMenu(
  el: HTMLElement,
  mobileActiveClass: string,
  maskActiveClass: string,
  hamburgerToggleEl: HTMLElement,
  backgroundColor: string,
  menuEl: HTMLElement,
  bodyEl: HTMLElement,
  subMenuSelector: string,
  subMenuActiveClass: string,
  subMenuToggleSelector: string,
  dropdownToggleSelector: string,
  dropdownToggleActiveClass: string
) {
  el.classList.remove(mobileActiveClass)
  el.classList.remove(maskActiveClass)
  hamburgerToggleEl.setAttribute("aria-expanded", "false")
  if (backgroundColor && bodyEl.classList.contains("mobile-menu-open")) {
    removeNavBgColor(el, backgroundColor)
  }
  bodyEl.classList.remove("mobile-menu-open")
  if (window.innerWidth > 767) {
    menuEl.style.height = "auto"
  }
  focusLock.off(menuEl)
  // Close any a) active drawers and b) active submenus
  const activeMenuEls = el.querySelectorAll(dropdownToggleSelector)
  if (activeMenuEls !== null) {
    activeMenuEls.forEach(el => {
      if (el.classList.contains(dropdownToggleActiveClass)) {
        el.classList.remove(dropdownToggleActiveClass)
      }
    })
  }
  const activeSubMenuEls = el.querySelectorAll(subMenuSelector)
  if (activeSubMenuEls !== null) {
    activeSubMenuEls.forEach(el => {
      let subMenuToggleEl = el.querySelector(subMenuToggleSelector)
      if (subMenuToggleEl !== null) {
        subMenuToggleEl.setAttribute("aria-expanded", "false")
        if (el.classList.contains(subMenuActiveClass)) {
          el.classList.remove(subMenuActiveClass)
        }
      }
    })
  }
}
